import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Spinner, Input, Label } from 'reactstrap';
import {
  FaChartLine,
  FaBoxOpen,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaCashRegister,
  FaTools,
  FaUndo,
  FaReceipt,
  FaStore,
  FaToolbox,
  FaChartArea,
  FaChartPie
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { saleActions } from '../../sagas/saleSlice';
import Swal from 'sweetalert2';
import { formatPrice } from "../../helpers/utility";

const Dashboard = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const role = useSelector((state) => state.auth.role);

  const {
    totalVentes,
    totalAchats,
    valeurStock,
    totalAvoirs,
    totalVentesBL,
    totalVentesDirect,
    totalBlImpayee,
    totalAchatsImpayees,
    totalPaiementsClient,
    lowStockItems,
    isLoading,
    totalExpenses,
    totalPaiementsFournisseur,
    totalRetourFournisseur,
    totalPaiements,
  } = useSelector((state) => state.sales);

  const pad = (n) => String(n).padStart(2, '0');
  const toISODate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [searchLowStock, setSearchLowStock] = useState("");

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const [startDate, setStartDate] = useState(toISODate(today));
  const [endDate, setEndDate] = useState(toISODate(today));

  useEffect(() => {
    if (!startDate || !endDate) return;
    dispatch(saleActions.getSalesData({ startDate, endDate }));
  }, [dispatch, startDate, endDate]);

  const validateDates = () => {
    if (new Date(startDate) > new Date(endDate)) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Invalid date range' });
      return false;
    }
    return true;
  };

  const filteredLowStock = useMemo(() => {
    if (!searchLowStock) return lowStockItems;

    const q = searchLowStock.toLowerCase();

    return lowStockItems.filter(item =>
      (item.code || "").toLowerCase().includes(q) ||
      (item.categorieLibelle || "").toLowerCase().includes(q) ||
      (item.libelle || "").toLowerCase().includes(q)
    );
  }, [lowStockItems, searchLowStock]);

  const handleRefresh = () => {
    if (!validateDates()) return;
    dispatch(saleActions.getSalesData({ startDate, endDate }));
  };

  /** CARD COMPONENT */
  const StatCard = ({ icon: Icon, title, value, color }) => (
    <Card className="shadow-sm h-100" style={{ minHeight: 80 }}>
      <CardBody className="py-2 px-3">
        <div className="d-flex align-items-center mb-1">
          <Icon className={`me-2 text-${color}`} size={16} />
          <span className="fw-semibold" style={{ fontSize: "0.85rem" }}>
            {title}
          </span>
        </div>
        <h5 className="mb-0 fw-bold" style={{ fontSize: "1rem" }}>
          {formatPrice(value)}
        </h5>
      </CardBody>
    </Card>
  );

  return (
    <Container fluid>

      {/* DATE FILTERS */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <CardBody className="d-flex flex-wrap align-items-end justify-content-between gap-3">

              <div className="d-flex align-items-center gap-2">
                <FaChartPie className="text-primary" size={20} />
                <h4 className="mb-0">{t('dashboard.title')}</h4>
              </div>

              <div className="d-flex flex-wrap align-items-end gap-3">
                <div className="d-flex gap-2 align-items-center">
                  <Label className="fw-semibold mb-0">{t('dashboard.startDate')}</Label>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>

                <div className="d-flex gap-2 align-items-center">
                  <Label className="fw-semibold mb-0">{t('dashboard.endDate')}</Label>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>

                <Button color="primary" onClick={handleRefresh}>
                  {isLoading ? <Spinner size="sm" /> : t('common.apply')}
                </Button>
              </div>

            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ADMIN CARDS */}
      {role === 'ROLE_ADMIN' && (
        <>

          {/* ========================= */}
          {/* 🔵 FIRST LINE OF STATS     */}
          {/* ========================= */}
          <Row className="mb-4">

            <Col md="2">
              <StatCard
                icon={FaChartLine}
                title="Total Ventes"
                value={totalVentes}
                color="primary"
              />
            </Col>

            <Col md="2">
              <StatCard
                icon={FaStore}
                title="Ventes BL"
                value={totalVentesBL}
                color="info"
              />
            </Col>

            <Col md="2">
              <StatCard
                icon={FaStore}
                title="Ventes Comptoirs"
                value={totalVentesDirect}
                color="info"
              />
            </Col>

            <Col md="2">
              <StatCard
                icon={FaCashRegister}
                title="Paiements Clients"
                value={totalPaiementsClient}
                color="success"
              />
            </Col>

            <Col md="2">
              <StatCard
                icon={FaReceipt}
                title="BL Impayés"
                value={totalBlImpayee}
                color="danger"
              />
            </Col>

            <Col md="2">
              <StatCard
                icon={FaUndo}
                title="Retour Clients"
                value={totalAvoirs}
                color="warning"
              />
            </Col>


          </Row>

          {/* ========================= */}
          {/* 🔵 SECOND LINE OF STATS    */}
          {/* ========================= */}
          <Row className="mb-4">

            <Col md="2">
              <StatCard
                icon={FaFileInvoiceDollar}
                title="Total Achats"
                value={totalAchats}
                color="primary"
              />
            </Col>
            <Col md="2">
              <StatCard
                icon={FaToolbox}
                title="Dépenses"
                value={totalExpenses}
                color="dark"
              />
            </Col>

            <Col md="2">
              <StatCard
                icon={FaMoneyBillWave}
                title="Valeur Stock"
                value={valeurStock}
                color="secondary"
              />
            </Col>
            <Col md="2">
              <StatCard
                icon={FaCashRegister}
                title="Paiements Fourn."
                value={totalPaiementsFournisseur}
                color="success"
              />
            </Col>

            <Col md="2">
              <StatCard
                icon={FaReceipt}
                title="BR Impayés"
                value={totalAchatsImpayees}
                color="danger"
              />
            </Col>

            <Col md="2">
              <StatCard
                icon={FaUndo}
                title="Retour Fourn."
                value={totalRetourFournisseur}
                color="warning"
              />
            </Col>



          </Row>

        </>
      )}


      {/* LOW STOCK TABLE */}
      <Row className="mb-5">
        <Col>
          <Card className="shadow-sm">
            <CardBody>

              {/* HEADER + SEARCH BAR */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                  <FaBoxOpen className="text-warning me-2" size={20} />
                  <h5 className="mb-0">{t('dashboard.lowStockAlert')}</h5>
                </div>

                {/* 🔍 Search field */}
                <Input
                  type="text"
                  placeholder="Rechercher…"
                  value={searchLowStock || ""}
                  onChange={(e) => setSearchLowStock(e.target.value)}
                  style={{ width: 220 }}
                />
              </div>

              {/* TABLE */}
              <div className="table-responsive">
                <table className="table table-bordered table-sm table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Code</th>
                      <th>Catégorie</th>
                      <th>Désignation</th>
                      <th>Stock Minimal</th>
                      <th>Stock Disponible</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLowStock.map(item => (
                      <tr key={item.id}>
                        <td>{item.code}</td>
                        <td>{item.categorieLibelle}</td>
                        <td>{item.libelle}</td>
                        <td>{item.stockMinimal}</td>
                        <td className="text-danger fw-bold">{item.stockDisponible}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </CardBody>
          </Card>
        </Col>
      </Row>


    </Container>
  );
};

export default Dashboard;
